import { sortBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  HeaderSection,
  HeaderSectionLeft
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useReduxDispatch } from 'src/store';
import {
  GradeCorrectionDto,
  GradeDetailDto,
  GradeDto
} from 'src/store/grades/grade';
import { mapGradeDetailToEdit } from 'src/store/grades/grade.map';
import {
  editGradeCorrections,
  fetchGrades,
  GradeTypes
} from 'src/store/grades/grades.actions';
import { GradeApi } from 'src/store/grades/grades.api';
import { selectActiveCorrectedGrades } from 'src/store/grades/grades.selectors';
import { fetchInstruments } from 'src/store/instruments/instruments.actions';
import { isLoadingGrades } from 'src/store/requests/requests.selectors';
import { CorrectionForm } from './Correction/CorrectionForm';
import { CorrectionsList } from './CorrectionsList';

export const CorrectionsPage = () => {
  const [currentGrade, setCurrentGrade] = useState<GradeDetailDto | null>(null);
  const [grade, setGrade] = useState<Nullable<GradeDetailDto>>(null);
  const [grades, setGrades] = useState<GradeDetailDto[]>([]);
  const [corrections, setCorrections] = useState<GradeCorrectionDto[]>([]);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const grades$ = useSelector(selectActiveCorrectedGrades);
  const loadingGrades = useSelector(isLoadingGrades);

  useEffect(() => {
    dispatch(fetchInstruments());
    dispatch(fetchGrades());
  }, [dispatch]);

  useEffect(() => {
    if (grades$.length !== grades.length)
      setGrades(grades$ as GradeDetailDto[]);
  }, [grades$, grades.length]);

  useEffect(() => {
    setGrades(
      grades.map(grade =>
        grade.id === currentGrade?.id
          ? {
              ...grade,
              expanded: true,
              gradeCorrections: sortBy(
                currentGrade?.gradeCorrections ?? [],
                correction => correction.parameter?.name.toLowerCase(),
                'parameter.name'
              )
            }
          : grade
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGrade]);

  useEffect(() => {
    if (grade)
      setCorrections(
        sortBy(
          grade.gradeCorrections.filter(
            x =>
              x.parameter?.corrected === true &&
              x.instrument?.instrumentGroup.corrected === true
          ),
          '[parameter.name]'
        )
      );
  }, [grade]);

  const onCancel = () => {
    setCurrentGrade(null);
    setShowEditModal(false);
  };

  const onEdit = async (grade: GradeDto) => {
    const response = await GradeApi.getById(grade.id);
    if (response.ok) {
      setGrade(response.data);
      setShowEditModal(true);
    }
  };

  const onGradeClick = async (grade: GradeDetailDto & Expandable) => {
    if (grade.expanded) {
      setGrades(
        grades.map(grade =>
          grade.id === grade.id
            ? {
                ...grade,
                expanded: false
              }
            : grade
        )
      );
    } else {
      const response = await GradeApi.getById(grade.id);
      if (response.ok) {
        setCurrentGrade(response.data);
      }
    }
  };

  const onCorrectionsChanged = (corrections: GradeCorrectionDto[]) => {
    setCorrections(corrections);
  };

  const onSubmit = async () => {
    if (grade) {
      const updatedGrade = {
        ...grade,
        gradeCorrections: grade.gradeCorrections.map(gc => {
          const i = corrections.findIndex(c => c.id === gc.id);
          return i > -1
            ? { ...gc, correctionPart: corrections[i].correctionPart }
            : gc;
        })
      };
      setGrade(updatedGrade);
      const dto = mapGradeDetailToEdit(updatedGrade);
      const result = await dispatch(editGradeCorrections(dto.id, dto));
      if (result.type !== GradeTypes.Failure) {
        dispatch(fetchGrades());
        onCancel();
      }
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('grade.corrections')}</Header1>
        </HeaderSectionLeft>
      </HeaderSection>

      <CorrectionsList
        grades={grades}
        loading={loadingGrades}
        onEdit={onEdit}
        onGradeClick={onGradeClick}
      />

      <Modal
        header={t('grade.corrected')}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        width={864}
      >
        <CorrectionForm
          corrections={corrections}
          onCorrectionsChanged={onCorrectionsChanged}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      </Modal>
    </>
  );
};
